/**
 * Allowed Users Management
 * Determines which users are allowed to login based on project team members and stakeholders
 */

import { AppState, User, Project } from '../types';
import { loadState } from '../store';
import { useApp } from '../providers/AppProvider';

/**
 * Get all allowed user emails from the system
 * Includes all users from the global users list and project team members
 */
export function getAllowedUserEmails(): string[] {
  const { state } = useApp();
  const allowedEmails = new Set<string>();

  // Add all users from the global users list
  state.users.forEach(user => {
    if (user.email) {
      allowedEmails.add(user.email.toLowerCase());
    }
  });

  // Add project team members (in case there are users assigned to projects but not in global list)
  state.projects.forEach(project => {
    project.teamMembers.forEach(member => {
      const user = state.users.find(u => u.id === member.userId);
      if (user?.email) {
        allowedEmails.add(user.email.toLowerCase());
      }
    });
  });

  return Array.from(allowedEmails).sort();
}

/**
 * Check if a user email is allowed to login
 */
export function isUserAllowed(email: string): boolean {
  if (!email) return false;
  
  const allowedEmails = getAllowedUserEmails();
  console.log('Allowed Emails:', allowedEmails); // Debugging log
  console.log('Checking email:', email); // Debugging log
  return allowedEmails.includes(email.toLowerCase());
}

/**
 * Get all users who are allowed to login
 * Returns full user objects with their details
 */
export function getAllowedUsers(): User[] {
  const { state } = useApp();
  return state.users.filter(user => user.email);
}

/**
 * Get allowed users grouped by role
 */
export function getAllowedUsersByRole(): Record<string, User[]> {
  const users = getAllowedUsers();
  const grouped: Record<string, User[]> = {};

  users.forEach(user => {
    const role = user.role;
    if (!grouped[role]) {
      grouped[role] = [];
    }
    grouped[role].push(user);
  });

  return grouped;
}

/**
 * Get allowed users grouped by organization
 */
export function getAllowedUsersByOrganization(): Record<string, User[]> {
  const users = getAllowedUsers();
  const grouped: Record<string, User[]> = {};

  users.forEach(user => {
    const orgId = user.organizationId;
    if (!grouped[orgId]) {
      grouped[orgId] = [];
    }
    grouped[orgId].push(user);
  });

  return grouped;
}

/**
 * Export allowed emails list for documentation/debugging
 */
export function exportAllowedEmailsList(): string {
  const emails = getAllowedUserEmails();
  const { state } = useApp();
  
  let output = '# Allowed User Emails\n\n';
  output += `Total: ${emails.length} users\n\n`;
  
  // Group by organization
  const usersByOrg = getAllowedUsersByOrganization();
  
  Object.entries(usersByOrg).forEach(([orgId, users]) => {
    const org = state.organizations.find(o => o.id === orgId);
    output += `## ${org?.name || orgId}\n\n`;
    
    users
      .sort((a, b) => a.email.localeCompare(b.email))
      .forEach(user => {
        output += `- ${user.email} (${user.name}, ${user.role})\n`;
      });
    
    output += '\n';
  });
  
  return output;
}
